import { CashMargin, Side, TradeType } from 'config/consts';
import React, { CSSProperties } from 'react';
import {
  Sign,
  SignAddedCommaFormattedString,
  addSignCommaFormat,
  getSign,
} from '../../utils/formatter';
import { Link } from 'react-router-dom';
import * as consts from 'config/consts';
import {
  AccountType,
  AccountTypeLabel,
} from 'components/features/utils/accountType';
import { SideLabel } from 'components/features/utils/side';
import { formatNumber } from 'utils/formatter';
import {
  DataFetchingState,
  getDataFetchingState,
} from 'components/features/home/PortfolioStuffing/asset/PortfolioStuffingAsset';
import { getDataFetchingTextStyle_ } from 'components/features/home/PortfolioRatioStuffing/PortfolioRatioStuffingList';
import { DataFetching } from 'components/features/home/PortfolioStuffing/asset/AssetOrganizedPortfolioItem';
import { getSignColor } from 'components/features/new-report/yearly-report/YearlyReport';
import {
  OrderItemProps,
  yyyymmddToDate,
} from 'components/features/new-report/daily-report/DailyReport';
import {
  isForeignStockTypes,
  isQtyHiddenProductType,
  productTypesToString,
  productTypesToUnit,
} from 'components/features/utils/productTypes';
import { isGoSymbolDetail as isGoSymbolDetail_ } from 'components/features/utils/util';

export type RealizedProfitAndLossProps = {
  periodVerbose: string;
  totalProfitAndLoss: SignAddedCommaFormattedString;
  profitAndLossItemData: ProfitAndLossItemProps[];
  Title: React.FC<Record<string, unknown>>;
  totalProfitAndLossSign: Sign;
};

type TotalProfitAndLossProps = {
  totalProfitAndLoss: SignAddedCommaFormattedString;
  periodVerbose: string;
  totalProfitAndLossSign: Sign;
};

type ProfitAndLossItemProps = {
  numberOfStocks: number;
  stockDate: string | null;
  stockCode: string;
  stockName: string;
  profitAndLossSign: Sign;
  profitAndLossAmount: number | null;
  side: typeof Side[keyof typeof Side];
  tradeType: typeof TradeType[keyof typeof TradeType];
  cashMargin: typeof CashMargin[keyof typeof CashMargin];
  accountType: AccountType;
  productType: typeof consts.productTypes[keyof typeof consts.productTypes];
  foreign: {
    profitAndLossSign: Sign;
    profitAndLossAmount: number | null;
    currency: consts.Currency;
  } | null;
};

type ProfitAndLossListProps = {
  children: React.ReactNode;
};

const TotalProfitAndLoss: React.FC<TotalProfitAndLossProps> = ({
  totalProfitAndLoss,
  periodVerbose: period,
  totalProfitAndLossSign,
}) => {
  return (
    <div className="items-center flex font-bold justify-between m-4 rounded bg-gray-100">
      <div className="p-4 rounded">今{period}の合計損益</div>
      <div
        className={`p-4 whitespace-no-wrap flex ${getSignColor(
          totalProfitAndLossSign
        )}`}>
        {totalProfitAndLoss}
        <div className="">円</div>
      </div>
    </div>
  );
};

const LinkToOrderHistory: React.FC<Record<string, unknown>> = () => {
  const orderHistoryLink = '/tradehistory';
  return (
    <div className="p-4 overflow-hidden">
      <div className="w-full flex items-center p-0">
        <Link
          className="h-5 flex items-center ml-auto text-sm text-blue-700"
          to={orderHistoryLink}>
          <div className="icon-right"></div>
          取引履歴へ
        </Link>
      </div>
      <div className="py-4 px-0 text-xs text-bluegray-500">
        ※上記取引履歴は「みんかぶ口座連携」で連携中の口座のみ参照することができます。
      </div>
    </div>
  );
};

const ProfitAndLossList: React.FC<ProfitAndLossListProps> = ({ children }) => {
  return (
    <>
      <ul className="">{children}</ul>
    </>
  );
};

const ProfitAndLossItem: React.FC<ProfitAndLossItemProps> = ({
  numberOfStocks,
  stockDate,
  stockCode,
  stockName,
  profitAndLossSign,
  profitAndLossAmount,
  side,
  cashMargin,
  foreign,
  accountType,
  productType,
}) => {
  const itemLink = `tradedetail/${stockCode}`;
  const dataFetchingState: DataFetchingState = getDataFetchingState(
    stockCode,
    productType === consts.productTypes.投資信託
      ? consts.AssetType['投資信託']
      : consts.AssetType['その他']
  );
  const isGoSymbolDetail =
    dataFetchingState === 'Fetched' &&
    isGoSymbolDetail_(stockCode, productType);

  const LinkContainer = isGoSymbolDetail
    ? ({
        className,
        children,
        style,
      }: {
        className: string;
        children: React.ReactNode;
        style: CSSProperties;
      }) => (
        <Link to={itemLink} {...{ className, style }}>
          {children}
        </Link>
      )
    : ({
        children,
        className,
        style,
      }: {
        className: string;
        children: React.ReactNode;
        style: CSSProperties;
      }) => (
        <div {...{ className }} style={style}>
          {children}
        </div>
      );
  const getDataFetchingTextStyle = getDataFetchingTextStyle_(dataFetchingState);

  const isProfitAndLossHidden =
    cashMargin === consts.CashMargin.現物 &&
    side === consts.Side.買 &&
    profitAndLossAmount === null;

  return (
    <>
      <LinkContainer
        className={`flex items-center justify-between py-3 border-t border-bluegray-200`}
        style={foreign ? { backgroundImage: 'url()' } : {}}>
        <div className="ml-0 flex-1 min-w-0">
          {dataFetchingState === 'Fetching' && (
            <div className="flex flex-row mb-1">
              <DataFetching />
            </div>
          )}
          <div className="h-10 flex flex-row items-center mb-1">
            <SideLabel side={side} />
            <div className="flex flex-col ml-2">
              <AccountTypeLabel {...{ accountType }} />
              <div className="flex flex-row items-center text-xs">
                <div className="text-bluegray-700">
                  {productTypesToString(productType, cashMargin)}
                </div>
                {!isQtyHiddenProductType(productType) ? (
                  <div
                    className={`ml-4 mr-2 ${getDataFetchingTextStyle(
                      'text-bluegray-700'
                    )}`}>
                    {formatNumber(numberOfStocks, {
                      unitName: '',
                      noValueText: '---',
                    })}
                    {productTypesToUnit(productType)}
                  </div>
                ) : (
                  <></>
                )}
              </div>
            </div>
            <div
              className={`ml-auto ${getDataFetchingTextStyle(
                'text-bluegray-500'
              )} text-sm`}>
              {stockDate}
            </div>
          </div>
          <div className="w-full flex flex-row items-end">
            <div
              className={`mt-1 mr-2 line-clamp-2 text-sm font-bold ${getDataFetchingTextStyle(
                ''
              )}`}>
              {stockName}
            </div>
            {!isProfitAndLossHidden && (
              <div
                className={`${getDataFetchingTextStyle(
                  getSignColor(profitAndLossSign)
                )} flex items-center ml-auto flex-shrink text-sm font-bold`}
                style={{ whiteSpace: 'nowrap' }}>
                {addSignCommaFormat(profitAndLossAmount)}
                <div className="unit ml-1">円</div>
              </div>
            )}
          </div>
          {foreign && !isProfitAndLossHidden && (
            <div className="h-6 flex items-center text-sm">
              <div
                className={`ml-auto ${getSignColor(
                  foreign.profitAndLossSign
                )}`}>
                (
                {addSignCommaFormat(foreign.profitAndLossAmount, {
                  numberOfDecimals: 2,
                })}{' '}
                {foreign.currency})
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-row justify-center items-center">
          <div
            className={`icon-chevron-right text-lg pl-4 ${
              isGoSymbolDetail ? '' : 'text-transparent'
            }`}></div>
        </div>
      </LinkContainer>
    </>
  );
};

export const RealizedProfitAndLoss = ({
  from,
  Orders,
}: {
  from: number;
  Orders: OrderItemProps[];
}) => {
  const month = (yyyymmddToDate(from.toString()) || new Date()).getMonth() + 1;
  const periodVerbose = '月';
  const Title = getTitle(month, periodVerbose);
  const totalProfitAndLoss_ = Orders.reduce((acc, data) => {
    return acc + (data.profitOrLoss ?? 0);
  }, 0);

  const totalProfitAndLoss = addSignCommaFormat(totalProfitAndLoss_);
  const totalProfitAndLossSign = getSign(totalProfitAndLoss_);
  const profitAndLossItemData = Orders.map(data => {
    const tradeTime =
      data.tradeTime !== null
        ? new Date(data.tradeTime.replace(' ', 'T'))
        : null;

    return {
      numberOfStocks: data.qty,
      stockDate:
        tradeTime === null
          ? null
          : `${tradeTime.getFullYear()}/${tradeTime.getMonth() +
              1}/${tradeTime.getDate()}`,
      stockCode: data.symbol,
      stockName: data.symbolName,
      profitAndLossSign: getSign(data.profitOrLoss ?? 0),
      profitAndLossAmount: data.profitOrLoss,
      side: data.side as typeof Side[keyof typeof Side],
      tradeType: data.tradeType as typeof TradeType[keyof typeof TradeType],
      cashMargin: data.cashMargin as typeof CashMargin[keyof typeof CashMargin],
      accountType: data.accountType as AccountType,
      productType: data.productType as typeof consts.productTypes[keyof typeof consts.productTypes],
      foreign: isForeignStockTypes(data.productType)
        ? {
            currency: data.currencyUnit ?? '',
            profitAndLossAmount: data.profitOrLossUSD,
            profitAndLossSign: getSign(data.profitOrLossUSD ?? 0),
          }
        : null,
    };
  }) as ProfitAndLossItemProps[];

  return (
    <>
      <div className="p-4 overflow-hidden">
        <Title />
        <TotalProfitAndLoss
          totalProfitAndLoss={totalProfitAndLoss}
          periodVerbose={periodVerbose}
          totalProfitAndLossSign={totalProfitAndLossSign}
        />
        {!isRealizedProfitAndLossEmpty(profitAndLossItemData) ? (
          <ProfitAndLossList>
            {profitAndLossItemData.map((p, i) => (
              <ProfitAndLossItem {...p} key={`${p.stockCode}${i}`} />
            ))}
            <div className="border-b border-bluegray-200"></div>
          </ProfitAndLossList>
        ) : null}
        <LinkToOrderHistory />
      </div>
    </>
  );
};

export const isRealizedProfitAndLossEmpty = (
  profitAndLossItemData: ProfitAndLossItemProps[]
): boolean => {
  return profitAndLossItemData.length === 0;
};

const getTitle = (
  month: number,
  periodVerbose: string
): React.FC<Record<string, unknown>> => (): ReturnType<
  React.FC<Record<string, unknown>>
> => (
  <div className="flex items-center mb-3 text-base font-bold text-bluegray-700">
    <img src="/static/images/report/icon-file.svg" alt="" className="mr-2" />
    {month}
    {periodVerbose}の確定損益
  </div>
);
